/** Parte-a-todo por etapa del ciclo de vida. Horizontal: los nombres de producto son largos.
 *  Categórico en orden fijo, 2px de hueco entre segmentos, y la etapa dominante etiquetada. */
import { useState } from "react";
import type { Grafico, Idioma, SerieApilada } from "../lib/tipos";
import { num } from "../lib/formato";
import { TEXTOS } from "../lib/i18n";
import { ANCHO_MOVIL, Leyenda, Marco, Tooltip, colorSerie, useEsMovil } from "./comun";

const ANCHO = 660;
const ALTO_BARRA = 40;
const IZQ = 150;
const HUECO = 2;

export function GraficoBarrasApiladas({ grafico, idioma }: { grafico: Grafico; idioma: Idioma }) {
  const series = (grafico.series ?? []) as SerieApilada[];
  const etapas = grafico.etapas_ids ?? [];
  // Los ids de etapa son claves de `s.etapas`; lo que se MUESTRA es `etiquetas`, que el motor
  // manda ya en el idioma del turno. Sin esto la leyenda decía "fabricacion_papel".
  const nombreEtapa = (i: number) => grafico.etiquetas?.[i] ?? etapas[i] ?? "";
  const txt = TEXTOS[idioma];
  const [activo, setActivo] = useState<{ s: number; e: number } | null>(null);
  const movil = useEsMovil();
  if (!series.length || !etapas.length) return null;

  const max = Math.max(...series.map((s) => s.total)) || 1;
  // Los nombres de producto son largos («Zewa Best 3-ply 200 sheets»): en móvil van encima de su
  // barra, no a la izquierda, o no quedaría barra que comparar.
  const anchoV = movil ? ANCHO_MOVIL : ANCHO;
  const izq = movil ? 0 : IZQ;
  const altoBarra = movil ? 30 : ALTO_BARRA;
  const altoEtiqueta = movil ? 17 : 0;
  const paso = altoBarra + altoEtiqueta + 14;
  const cuerpo = movil ? 13 : 11;
  const utilAncho = anchoV - izq - (movil ? 4 : 90);
  const alto = 26 + series.length * paso;

  return (
    <>
      <Marco titulo={grafico.titulo} idioma={idioma} ancho={anchoV} alto={alto}>
        <svg viewBox={`0 0 ${anchoV} ${alto}`} width="100%" height="100%" onMouseLeave={() => setActivo(null)}>
          {series.map((s, si) => {
            let x = izq;
            const fila = 16 + si * paso;
            const y = fila + altoEtiqueta;
            return (
              <g key={s.producto_id}>
                <text x={movil ? 0 : IZQ - 10} y={movil ? fila + 11 : y + altoBarra / 2}
                      textAnchor={movil ? "start" : "end"} dominantBaseline={movil ? undefined : "middle"}
                      fontSize={cuerpo} fill="var(--texto-1)">
                  {s.nombre.length > (movil ? 32 : 22) ? `${s.nombre.slice(0, (movil ? 32 : 22) - 1)}…` : s.nombre}
                </text>
                {etapas.map((etapa, ei) => {
                  const v = s.etapas[etapa] ?? 0;
                  const w = Math.max((v / max) * utilAncho - HUECO, 0);
                  const izqSeg = x;
                  x += (v / max) * utilAncho;
                  const seleccionado = activo?.s === si && activo?.e === ei;
                  const primero = ei === 0;
                  const ultimo = ei === etapas.length - 1;
                  return (
                    <rect key={etapa} x={izqSeg} y={y} width={w} height={altoBarra}
                          rx={primero || ultimo ? 4 : 0}
                          fill={colorSerie(ei)} fillOpacity={activo === null || seleccionado ? 1 : 0.5}
                          stroke={seleccionado ? "var(--texto-1)" : "none"} strokeWidth="1.5"
                          onMouseEnter={() => setActivo({ s: si, e: ei })} />
                  );
                })}
                {/* Etiqueta directa del total: el aviso de contraste del validador exige relief. */}
                {/* El total: a la derecha de la barra en escritorio; en móvil en la línea del
                    nombre, alineado a la derecha. */}
                <text x={movil ? anchoV : izq + utilAncho + 10} y={movil ? fila + 11 : y + altoBarra / 2}
                      dominantBaseline={movil ? undefined : "middle"} textAnchor={movil ? "end" : "start"}
                      fontSize={cuerpo} fill="var(--texto-1)">
                  {num(s.total, s.total < 1 ? 3 : 2)}
                </text>
                <text x={izq + 6} y={y + altoBarra + 11} fontSize={movil ? 11 : 9} fill="var(--texto-3)">
                  {s.dominante} {num(s.dominante_pct, 0)} %
                </text>
              </g>
            );
          })}
          {activo && series[activo.s] && etapas[activo.e] && (
            <Tooltip x={izq + 40} y={16 + activo.s * paso + altoEtiqueta} ancho={anchoV}
                     lineas={[series[activo.s]!.nombre, `${nombreEtapa(activo.e)}: ${num(series[activo.s]!.etapas[etapas[activo.e]!] ?? 0, 4)} ${grafico.unidad ?? ""}`,
                              `${num(((series[activo.s]!.etapas[etapas[activo.e]!] ?? 0) / series[activo.s]!.total) * 100, 1)} ${txt.pctDelTotal}`]} />
          )}
        </svg>
      </Marco>
      <Leyenda items={etapas.map((_e, i) => ({ nombre: nombreEtapa(i), color: colorSerie(i) }))} />
    </>
  );
}
